import {getGeoLocation} from '@/lib/locale/geoLocaltion'
import type {SupportedLocales} from '@/lib/locale/locale'
import {getProductDetailsQueryKey} from '@/lib/product/hooks/useProductDetails'
import {
  type MultiFullProduct,
  normalizeProductId,
  parseRetailer,
  type Product,
  type RawProduct,
  transformMultiFullProduct,
  transformProduct,
} from '@/models/productModel'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'

export async function getPriceHistory({
  locale,
  productId,
  retailer,
}: {
  locale: SupportedLocales
  productId: RawProduct['id']
  retailer: RawProduct['retailer']
}): Promise<[Date, number][]> {
  return call
    .post('/api/model/price_history/', {
      localization: {
        locale,
        geo_location: getGeoLocation(),
      },
      data: {
        retailer,
        id: productId,
      },
    })
    .then(res => {
      let parsedData: Record<string, number>
      if (typeof res.data !== 'string') {
        parsedData = res.data as Record<string, number>
      } else {
        parsedData = JSON.parse(res.data.replace(/\bNaN\b/g, '"***NaN***"'), (key, value) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- its safe
          return value === '***NaN***' ? NaN : value
        }) as Record<string, number>
      }
      return Object.entries(parsedData)
        .filter(([, y]) => !!y)
        .map(([x, y]) => [new Date(x), y])
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot parse or fetch price history', e)
      }
      return []
    }) as Promise<[Date, number][]>
}

export async function getProduct({
  signal,
  id,
  retailer,
  locale,
}: {
  signal?: AbortSignal
  id: RawProduct['id']
  retailer: RawProduct['retailer']
  locale: SupportedLocales
}): Promise<Product | false> {
  let r: Product['retailer']
  let i: Product['id']

  try {
    r = parseRetailer(retailer)
    i = normalizeProductId(id)
  } catch (e) {
    console.error('Cannot normalize product', e)
    return Promise.resolve(false)
  }

  return call
    .post<[RawProduct]>(
      '/api/model/product/',
      {
        data: {
          retailer: r,
          id: i,
        },
        localization: {
          geo_location: getGeoLocation(),
          locale,
        },
      },
      {
        signal,
      },
    )
    .then(res => {
      return transformProduct(res.data[0])
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch product', e)
      }
      return false
    })
}

export async function getProductDetails({
  locale,
  signal,
  id,
  retailer,
  preload,
  minimal,
  queryClient,
}: {
  locale: SupportedLocales
  signal?: AbortSignal
  id: RawProduct['id']
  retailer: RawProduct['retailer']
  preload?: boolean
  minimal?: boolean
  queryClient?: QueryClient
}): Promise<MultiFullProduct | false> {
  let r: Product['retailer']
  let i: Product['id']

  try {
    r = parseRetailer(retailer)
    i = normalizeProductId(id)
  } catch (e) {
    console.error('Cannot normalize product', e)
    return Promise.resolve(false)
  }

  const api = minimal ? '/api/model/plain_products/' : '/api/model/products/'

  return call
    .post(
      api,
      {
        localization: {
          locale,
          geo_location: getGeoLocation(),
        },
        data: {
          [r]: [i],
        },
      },
      {
        signal,
        fetchOptions: {
          priority: preload ? 'low' : 'high',
        },
      },
    )
    .then(res => {
      const result = transformMultiFullProduct(res.data, minimal)
      if (result) {
        queryClient?.setQueryData(
          getProductDetailsQueryKey({
            retailer,
            id,
            locale,
            minimal,
          }),
          result,
        )
      }
      return result
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch product details', e)
      }
      return false
    })
}

export async function getMinimalMultiProductDetails({
  locale,
  signal,
  products,
  preload,
}: {
  locale: SupportedLocales
  signal?: AbortSignal
  products: Pick<RawProduct, 'id' | 'retailer'>[]
  preload?: boolean
}): Promise<MultiFullProduct | false> {
  const pickData = new Map<RawProduct['retailer'], RawProduct['id'][]>()
  for (const product of products) {
    try {
      const r = parseRetailer(product.retailer)
      const i = normalizeProductId(product.id)
      if (!pickData.has(r)) {
        pickData.set(r, [])
      }
      pickData.get(r)?.push(i)
    } catch (e) {
      console.error('Cannot normalize product', e)
    }
  }

  return call
    .post(
      '/api/model/plain_products/',
      {
        localization: {
          locale,
          geo_location: getGeoLocation(),
        },
        data: Object.fromEntries(pickData),
      },
      {
        signal,
        fetchOptions: {
          priority: preload ? 'low' : 'high',
        },
      },
    )
    .then(res => {
      return transformMultiFullProduct(res.data, true)
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch product details', e)
      }
      return false
    })
}
